import { Link } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { EmptyState, ProgressBar, SectionCard } from "@/components/new-look/blocks";
import { NEEDS_YOU_TEXT, stageOf } from "@/components/new-look/stage";
import { useEpicRequests, useIdeaStageCounts } from "@/hooks/queries/use-epic-requests";
import { useEpics } from "@/hooks/queries/use-epics";
import { useMachines } from "@/hooks/queries/use-mailbox";
import { usePageTitle } from "@/hooks/use-page-title";
import { machineReadiness, type Machine, type MachineReadiness } from "@/lib/api/mailbox";
import type { Epic } from "@/lib/api/types";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils";
import { factsFor, updatedAgo } from "../ideas/idea-helpers";
import { YourCloud } from "./your-cloud";

/**
 * The new look's Dashboard (docs/new-look/PLAN.md, step 6.1): what needs you,
 * what's building now, your machines, what shipped this week, and four counters.
 */

const WEEK_MS = 7 * 24 * 3600_000;

/** Blueprints with a planning or build run going (or paused on someone). */
export function liveBuilds(epics: readonly Epic[]): Epic[] {
  return epics.filter((e) => e.facts?.buildJob != null || e.facts?.planJob != null);
}

/** Blueprints finished in the last seven days, newest first. */
export function shippedThisWeek(epics: readonly Epic[], now: number = Date.now()): Epic[] {
  const when = (e: Epic) => new Date(e.implementedDate ?? e.updatedAt).getTime();
  return epics
    .filter((e) => e.state === "implemented" && now - when(e) <= WEEK_MS)
    .sort((a, b) => when(b) - when(a));
}

const READINESS: Record<MachineReadiness, { label: string; dot: string }> = {
  ready: { label: "Ready", dot: "bg-green-500" },
  "runtime-not-found": { label: "No AI tool found", dot: "bg-amber-500" },
  outdated: { label: "Needs an update", dot: "bg-amber-500" },
  offline: { label: "Offline", dot: "bg-muted-foreground/50" },
};

function greeting(hour: number): string {
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function Counter({ label, value, to }: { label: string; value: number | undefined; to: string }) {
  return (
    <Link
      to={to}
      className="flex flex-col gap-1 rounded-2xl border border-border bg-background p-4 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-3xl font-bold tabular-nums text-foreground">{value ?? "—"}</span>
    </Link>
  );
}

function RowLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <li>
      <Link
        to={to}
        className="flex min-h-12 items-center gap-3 rounded-lg px-2 py-2 hover:bg-muted"
      >
        {children}
      </Link>
    </li>
  );
}

function Skeleton() {
  return <div className="h-24 animate-pulse rounded-lg bg-muted" />;
}

function NeedsYou({ me }: { me: string | undefined }) {
  const { data, isLoading, isError } = useEpicRequests({
    needsYou: true,
    withFacts: true,
    limit: 6,
    orderBy: "updatedAt",
    orderDirection: "desc",
    ...(me ? { requestedById: me } : {}),
  });
  const ideas = data?.pages[0]?.data ?? [];
  return (
    <SectionCard
      title="Needs you"
      action={
        <Link to="/ideas?view=needs" className="text-sm text-primary hover:underline">
          See all
        </Link>
      }
      className={cn(
        ideas.length > 0 &&
          "border-amber-300 bg-amber-50/60 dark:border-amber-500/40 dark:bg-amber-500/10"
      )}
    >
      {isLoading ? (
        <Skeleton />
      ) : isError ? (
        <p className="text-sm text-muted-foreground">Couldn't load this.</p>
      ) : ideas.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing is waiting for you.</p>
      ) : (
        <ul className="flex flex-col">
          {ideas.map((idea) => {
            const need = stageOf(factsFor(idea)).needsYou;
            return (
              <RowLink key={idea.id} to={`/ideas/${idea.id}`}>
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-[15px] font-semibold text-foreground">
                    {idea.title}
                  </span>
                  <span className="text-[13px] text-amber-900 dark:text-amber-200">
                    {need ? NEEDS_YOU_TEXT[need] : "Needs you"}
                  </span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              </RowLink>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}

function LiveBuilds({ epics, loading }: { epics: Epic[]; loading: boolean }) {
  return (
    <SectionCard title="Building now">
      {loading ? (
        <Skeleton />
      ) : epics.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing is running right now.</p>
      ) : (
        <ul className="flex flex-col">
          {epics.map((e) => {
            const parts = e.facts?.epic?.featureCount ?? 0;
            const done = e.facts?.epic?.completedFeatureCount ?? 0;
            const planning = !e.facts?.buildJob;
            return (
              <RowLink key={e.id} to={`/blueprints/${e.id}`}>
                <span
                  aria-hidden="true"
                  className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-blue-500"
                />
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="truncate text-[15px] font-semibold text-foreground">
                    {e.name}
                  </span>
                  <span className="text-[13px] text-muted-foreground">
                    {planning
                      ? "Making the blueprint"
                      : parts > 0
                        ? `${String(done)} of ${String(parts)} parts done`
                        : "Building"}
                    {e.team?.name ? ` · ${e.team.name}` : ""}
                  </span>
                  {!planning && parts > 0 && (
                    <ProgressBar
                      value={(done / parts) * 100}
                      label={`${e.name} progress`}
                      className="max-w-xs"
                    />
                  )}
                </span>
              </RowLink>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}

function Machines() {
  const { data, isLoading, isError } = useMachines();
  const machines: Machine[] = data ?? [];
  return (
    <SectionCard
      title="Your machines"
      action={
        <Link to="/settings" className="text-sm text-primary hover:underline">
          Manage
        </Link>
      }
    >
      {isLoading ? (
        <Skeleton />
      ) : isError ? (
        <p className="text-sm text-muted-foreground">Couldn't load your machines.</p>
      ) : machines.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No machine connected yet. Connect one in Settings to build on it.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {machines.map((m) => {
            const r = READINESS[machineReadiness(m)];
            return (
              <li key={m.id} className="flex min-h-10 items-center gap-3 px-2">
                <span
                  aria-hidden="true"
                  className={cn("h-2.5 w-2.5 shrink-0 rounded-full", r.dot)}
                />
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                  {m.name}
                </span>
                <span className="shrink-0 text-[13px] text-muted-foreground">{r.label}</span>
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}

const SHIPPED_SHOWN = 8;

function Shipped({ epics, loading }: { epics: Epic[]; loading: boolean }) {
  return (
    <SectionCard
      title="Shipped this week"
      action={
        epics.length > SHIPPED_SHOWN ? (
          <Link to="/blueprints" className="text-sm text-primary hover:underline">
            See all
          </Link>
        ) : undefined
      }
    >
      {loading ? (
        <Skeleton />
      ) : epics.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nothing shipped in the last seven days.</p>
      ) : (
        <ul className="flex flex-col">
          {epics.slice(0, SHIPPED_SHOWN).map((e) => (
            <RowLink key={e.id} to={`/blueprints/${e.id}`}>
              <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
                {e.name}
              </span>
              <span className="shrink-0 text-[13px] text-muted-foreground">
                {updatedAgo(e.implementedDate ?? e.updatedAt)}
              </span>
            </RowLink>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}

export function HomeDashboardPage() {
  usePageTitle("Dashboard");
  const user = useAuthStore((s) => s.user);
  const { data: counts } = useIdeaStageCounts();
  const epicsQuery = useEpics({
    limit: 100,
    withFacts: true,
    orderBy: "updatedAt",
    orderDirection: "desc",
  });
  const epics = epicsQuery.data?.pages.flatMap((p) => p.data) ?? [];
  const firstName = user?.name.split(" ")[0];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            {greeting(new Date().getHours())}
            {firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            What needs you, what's building, and what shipped.
          </p>
        </div>
        <Link
          to="/ideas/new"
          className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          New idea
        </Link>
      </div>

      <section aria-label="Counts" className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Counter label="Ideas being shaped" value={counts?.idea} to="/ideas" />
        <Counter label="Blueprints" value={counts?.blueprint} to="/blueprints" />
        <Counter label="Building" value={counts?.build} to="/ideas?view=building" />
        <Counter label="Shipped" value={counts?.done} to="/ideas?view=shipped" />
      </section>

      {epicsQuery.isError ? (
        <EmptyState
          title="Couldn't load your work"
          body="Check your connection and refresh the page."
        />
      ) : (
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-start">
          <div className="flex min-w-0 flex-col gap-5">
            <NeedsYou me={user?.id} />
            <LiveBuilds epics={liveBuilds(epics)} loading={epicsQuery.isLoading} />
          </div>
          <aside className="flex min-w-0 flex-col gap-5">
            <Machines />
            <YourCloud />
            <Shipped epics={shippedThisWeek(epics)} loading={epicsQuery.isLoading} />
          </aside>
        </div>
      )}
    </div>
  );
}
